import React, { useState, useEffect } from "react";
import {
  Page,
  Card,
  Layout,
  FormLayout,
  Select,
  Button,
  Banner,
  Spinner,
} from "@shopify/polaris";
import axios from "../axiosInstance";

const MONTHS = [
  { label: "January", value: "1" },
  { label: "February", value: "2" },
  { label: "March", value: "3" },
  { label: "April", value: "4" },
  { label: "May", value: "5" },
  { label: "June", value: "6" },
  { label: "July", value: "7" },
  { label: "August", value: "8" },
  { label: "September", value: "9" },
  { label: "October", value: "10" },
  { label: "November", value: "11" },
  { label: "December", value: "12" },
];

export default function CommissionReport() {
  const now = new Date();
  const CURRENT_YEAR = now.getFullYear();
  const CURRENT_MONTH = now.getMonth() + 1;

  const [month, setMonth] = useState(String(CURRENT_MONTH));
  const [year, setYear] = useState(String(CURRENT_YEAR));
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState({ error: false, message: "" });
  const [yearsOptions, setYearsOptions] = useState([]);

  useEffect(() => {
    const options = [];
    for (let y = CURRENT_YEAR; y >= 2020; y--) {
      options.push({ label: String(y), value: String(y) });
    }
    setYearsOptions(options);
  }, []);

  const handleGenerateReport = async () => {
    const selectedYear = parseInt(year, 10);
    const selectedMonth = parseInt(month, 10);

    if (!selectedMonth) {
      return setStatus({ error: true, message: "Month is required." });
    }

    if (!selectedYear) {
      return setStatus({ error: true, message: "Year is required." });
    }

    if (
      selectedYear > CURRENT_YEAR ||
      (selectedYear === CURRENT_YEAR && selectedMonth > CURRENT_MONTH)
    ) {
      return setStatus({
        error: true,
        message: "Cannot generate a report for a future month.",
      });
    }

    setLoading(true);
    setStatus({ error: false, message: "" });

    try {
      const { data } = await axios.post("/api/commissions/report", {
        year: selectedYear,
        month: selectedMonth,
      });

      if (data?.status) {
        setStatus({ error: false, message: data.msg });
      } else {
        setStatus({
          error: true,
          message: data?.msg || "Failed to initiate report generation.",
        });
      }
    } catch (error) {
      console.error("Error generating commission report:", error);

      const errorMsg =
        error?.response?.data?.msg ||
        error?.response?.data?.errMsg ||
        error?.message ||
        "An unexpected error occurred.";

      setStatus({ error: true, message: errorMsg });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Page breadcrumbs={[{ url: "/" }]} title="Commission Report" fullWidth>
      <Layout>
        <Layout.Section>
          <Card sectioned>
            <FormLayout>
              <FormLayout.Group>
                <Select
                  label="Month"
                  options={MONTHS}
                  onChange={setMonth}
                  value={month}
                  disabled={loading}
                  requiredIndicator
                />
                <Select
                  label="Year"
                  options={yearsOptions}
                  onChange={setYear}
                  value={year}
                  disabled={loading}
                  requiredIndicator
                />
              </FormLayout.Group>

              <Button
                primary
                onClick={handleGenerateReport}
                loading={loading}
                disabled={loading}
              >
                Generate Report
              </Button>
            </FormLayout>
          </Card>
        </Layout.Section>

        {status.message && (
          <Layout.Section>
            <Banner
              title={status.error ? "Error" : "Success"}
              status={status.error ? "critical" : "success"}
            >
              <p>{status.message}</p>
            </Banner>
          </Layout.Section>
        )}

        {loading && (
          <Layout.Section>
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                marginTop: "20px",
              }}
            >
              <Spinner size="large" accessibilityLabel="Generating report..." />
              <span style={{ marginLeft: "10px" }}>
                Processing can take a few minutes...
              </span>
            </div>
          </Layout.Section>
        )}
      </Layout>
    </Page>
  );
}